/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import Container from "@/src/components/Container/Container";
import Grid from "@mui/material/Grid";
import LinkLegalbird from "@/src/components/legalbird/LinkLegalbird/LinkLegalbird";
import { ArrowBack } from "@mui/icons-material";
import { Box, Stack, Typography } from "@mui/material";
import React, { Fragment, ReactNode } from "react";
import jobStyles from "@/src/components/Jobs/Job/jobStyles";
import MuiListNativeStyled from "@/src/components/List/MuiListNativeStyled";

export type JobContainerProps = {
  title: string;
  meta?: ReactNode;
  hardFacts: Array<{
    title: string;
    value: string;
  }>;
  content: Array<
    | {
        type: "text";
        text: ReactNode;
      }
    | {
        type: "headline";
        headline: string;
      }
    | {
        type: "list";
        items: string[];
      }
  >;
  afterContent?: ReactNode;
};

export default function JobContainer({ title, hardFacts, afterContent, content, meta }: JobContainerProps) {
  return (
    <Container
      variant={"lg"}
      sx={(theme) => ({
        paddingBottom: "6rem",
        [theme.breakpoints.up("lg")]: {
          paddingBottom: "11rem",
        },
      })}
    >
      <Stack spacing={6}>
        <Stack spacing={6}>
          <LinkLegalbird to={"/jobs"} standalone sx={{ width: "fit-content" }}>
            <ArrowBack /> Zurück zur Übersicht
          </LinkLegalbird>
          <Typography component={"h1"} variant={"h2"} sx={{ hyphens: "auto", overflowWrap: "anywhere" }}>
            {title}
          </Typography>
        </Stack>
        <Grid container spacing={{ xs: 3, lg: 16 }}>
          <Grid size={{ xs: 12, lg: 4 }}>
            <Box
              sx={(theme) => ({
                [theme.breakpoints.up("lg")]: {
                  position: "sticky",
                  top: "7rem",
                },
              })}
            >
              {hardFacts.map((hardFact) => (
                <Stack
                  key={hardFact.title}
                  spacing={1}
                  sx={(theme) => ({
                    borderTop: `1px solid ${theme.palette.divider}`,
                    paddingBlock: "0.75rem",
                    [theme.breakpoints.up("lg")]: {
                      paddingBlock: "1rem",
                    },
                  })}
                >
                  <Typography>
                    <strong>{hardFact.title}</strong>
                  </Typography>
                  <Typography>{hardFact.value}</Typography>
                </Stack>
              ))}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, lg: 8 }}>
            {meta}
            {content.map((item, idx) => {
              let content: ReactNode | null = null;

              switch (item.type) {
                case "headline":
                  content = (
                    <Typography component={"h2"} variant={"h6"} sx={jobStyles.text}>
                      {item.headline}
                    </Typography>
                  );
                  break;
                case "text":
                  content = <Typography sx={jobStyles.text}>{item.text}</Typography>;
                  break;
                case "list":
                  content = <MuiListNativeStyled sx={jobStyles.list} listItems={item.items} />;
                  break;
              }

              return <Fragment key={idx}>{content}</Fragment>;
            })}
            {afterContent}
          </Grid>
        </Grid>
      </Stack>
    </Container>
  );
}
